<template>
    <section class="single-img-section">
        <div class="single-img-section__container">
            <!-- 标题区域 -->
            <CardTitle v-if="!hideTitle" :module="item" />
            <!-- 展示图片区域（可能有多张） -->
            <div v-if="props.item.fieldData?.length > 0" class="single-img-section__gallery">
                <div v-for="(img, idx) in props.item.fieldData"
                    :key="String((img as Record<string, unknown>)?.id ?? idx)" class="single-img-section__gallery-item"
                    :style="getItemStyle(img)">
                    <p v-if="getCellTitle(img)" class="single-img-section__gallery-item-title">{{ getCellTitle(img) }}
                    </p>
                    <!-- 视频区域 -->
                    <video v-if="getVideoUrl(img)" :src="getVideoUrl(img)" controls autoplay controlslist="nodownload"
                        muted />
                    <!-- 图片区域 -->
                    <img v-if="getCellImage(img)" :src="getCellImage(img)" :alt="item.mainTitle" loading="lazy" />
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { type CSSProperties } from 'vue'
import type { Module } from '@/api/page_config'
import CardTitle from './CardTitle.vue'
import { getStrWithRecord } from '.';

const props = withDefaults(defineProps<{
    item: Module
    hideTitle?: boolean
}>(), { hideTitle: false })

function getCellImage(cell: unknown): string {
    return getStrWithRecord(cell, 'image') || ''
}

function getCellTitle(cell: unknown): string {
    return getStrWithRecord(cell, 'title') || ''
}

function getImageWidth(cell: unknown): string {
    return getStrWithRecord(cell, 'width') || ''
}

function getVideoUrl(cell: unknown): string {
    return getStrWithRecord(cell, 'video') || ''
}

function getItemStyle(cell: unknown): CSSProperties {
    const style: CSSProperties = {}
    const w = getImageWidth(cell)
    if (w) {
        const widthVal = w.endsWith('%') || w.endsWith('px') || w === 'auto' || w.endsWith('rem') || w.endsWith('vw')
            ? w
            : `${w}px`
        style.width = widthVal
        style.maxWidth = '100%'
        if (!w.endsWith('%')) {
            style.marginLeft = 'auto'
            style.marginRight = 'auto'
        }
    }
    // 单图 isFull=false 时在桌面端居中显示（不通过容器--full控制，用单图样式）
    // isFull=true 已由容器--full类处理
    return style
}
</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

.single-img-section {
    width: 100%;
}

.single-img-section__container {
    max-width: $container-max-width;
    margin: 0 auto;
    // 移动端：自动撑满，去除左右padding
    padding: 0 1rem;

    @media (min-width: $breakpoint-md) {
        // 桌面端非full模式：保留padding限制宽度
        padding: 0 2rem;
    }

    // 桌面端full模式：去除padding，图片全宽
    &--full {
        @media (min-width: $breakpoint-md) {
            max-width: none;
            padding: 0;
        }
    }
}

/* Gallery */
.single-img-section__gallery {
    display: flex;
    flex-direction: column;
    gap: 0;
    margin-bottom: 2rem;

    @media (min-width: $breakpoint-md) {
        // 桌面端非full模式：居中限宽，保留间距
        gap: 1.25rem;
        max-width: 900px;
        margin-left: auto;
        margin-right: auto;
        margin-bottom: 3.5rem;
    }

    // 桌面端full模式：全宽无圆角
    &--full {
        @media (min-width: $breakpoint-md) {
            max-width: none;
            gap: 1.25rem;

            .single-img-section__gallery-item img {
                border-radius: 0;
            }
        }
    }
}

.single-img-section__gallery-item {
    width: 100%;
    text-align: center;

    video,
    img {
        width: 100%;
        height: auto;
        display: block;
        object-fit: cover;
        // 移动端：无圆角全宽展示
        border-radius: 0;

        @media (min-width: $breakpoint-md) {
            // 桌面端：默认圆角（非full模式）
            border-radius: 1rem;
        }
    }
}

.single-img-section__gallery-item-title {
    font-size: 0.8rem;
    color: $color-text;
    margin-bottom: 1rem;
    text-align: left;

    @media (min-width: $breakpoint-md) {
        font-size: 1rem;
    }
}

.single-img-section__caption {
    font-size: 0.875rem;
    font-weight: 700;
    color: $color-text;
    margin: 0.75rem 0 0;
    text-align: center;

    @media (min-width: $breakpoint-md) {
        font-size: 1rem;
        margin-top: 1rem;
    }
}
</style>
